import type { AdminVideo, VideoCategory, VideoSourceType } from '@/mocks/adminVideos';
import { categoryLabels, categoryIcons, statusLabels, sourceTypeIcons } from '@/mocks/adminVideos';

interface VideoGridProps {
  videos: AdminVideo[];
  categories: VideoCategory[];
  filterCategory: VideoCategory | 'todas';
  onChangeCategory: (cat: VideoCategory | 'todas') => void;
  filterStatus: AdminVideo['status'] | 'todos';
  onChangeStatus: (st: AdminVideo['status'] | 'todos') => void;
  sourceFilter: VideoSourceType | 'todos';
  onChangeSource: (src: VideoSourceType | 'todos') => void;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onDelete: (video: AdminVideo) => void;
  onToggleFeatured: (video: AdminVideo) => void;
  onEdit: (video: AdminVideo) => void;
  onShare: (video: AdminVideo) => void;
  onHistory: (video: AdminVideo) => void;
  isPremium?: boolean;
}

export default function VideoGrid({
  videos,
  categories,
  filterCategory,
  onChangeCategory,
  filterStatus,
  onChangeStatus,
  sourceFilter,
  onChangeSource,
  searchQuery,
  onSearchChange,
  onDelete,
  onToggleFeatured,
  onEdit,
  onShare,
  onHistory,
  isPremium = false,
}: VideoGridProps) {
  return (
    <div className="space-y-4">
      {/* Filters bar */}
      <div className="flex flex-col gap-3">
        {/* Search */}
        <div className="relative">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por título o descripción..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-background-50 border border-background-200 text-foreground-900 text-sm placeholder:text-foreground-400 focus:outline-none focus:border-accent-500 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => onSearchChange('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-circle-fill text-sm"></i>
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Category chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => onChangeCategory('todas')}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                filterCategory === 'todas'
                  ? 'bg-accent-500 text-background-50'
                  : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-200'
              }`}
            >
              Todas
            </button>
            {categories.map((cat) => {
              const count = videos.filter((v) => v.category === cat).length;
              return (
                <button
                  key={cat}
                  onClick={() => onChangeCategory(cat)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                    filterCategory === cat
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  <i className={`${categoryIcons[cat]} text-[10px] mr-1`}></i>
                  {categoryLabels[cat]} ({count})
                </button>
              );
            })}
          </div>

          <span className="text-foreground-300 text-xs">|</span>

          {/* Status pills */}
          {(['todos', 'publicado', 'borrador', 'programado'] as const).map((st) => {
            const count = st === 'todos' ? videos.length : videos.filter((v) => v.status === st).length;
            return (
              <button
                key={st}
                onClick={() => onChangeStatus(st)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  filterStatus === st
                    ? 'bg-foreground-900 text-background-50'
                    : 'bg-background-50 border border-background-200 text-foreground-500 hover:bg-background-200'
                }`}
              >
                {st === 'todos' ? 'Todos' : statusLabels[st]} ({count})
              </button>
            );
          })}

          <span className="text-foreground-300 text-xs">|</span>

          {/* Source filter */}
          {(['todos', 'upload', 'youtube', 'facebook'] as const).map((src) => {
            const count = src === 'todos' ? videos.length : videos.filter((v) => v.sourceType === src).length;
            return (
              <button
                key={src}
                onClick={() => onChangeSource(src)}
                className={`px-2.5 py-1.5 rounded-full text-[11px] font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                  sourceFilter === src
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-50 border border-background-200 text-foreground-400 hover:bg-background-200'
                }`}
              >
                {src !== 'todos' && <i className={`${sourceTypeIcons[src]} text-[10px] mr-1`}></i>}
                {src === 'todos' ? 'Todos' : src === 'upload' ? 'Subidos' : src === 'youtube' ? 'YouTube' : 'Facebook'} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Results count */}
      <p className="text-xs text-foreground-400">
        {videos.length === 0 ? 'No se encontraron videos con los filtros actuales.' : `Mostrando ${videos.length} video${videos.length !== 1 ? 's' : ''}`}
      </p>

      {/* Empty state */}
      {videos.length === 0 && (
        <div className="text-center py-14 bg-background-50 rounded-2xl border border-background-200">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-background-200 flex items-center justify-center mb-3">
            <i className="ri-video-line text-foreground-400 text-xl"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-600">Sin videos</p>
          <p className="text-xs text-foreground-400 mt-1">
            Probá ajustar los filtros o agregá un nuevo video.
          </p>
        </div>
      )}

      {/* Video cards grid - desktop */}
      <div className="hidden md:grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {videos.map((video) => (
          <VideoCardDesktop
            key={video.id}
            video={video}
            onDelete={onDelete}
            onToggleFeatured={onToggleFeatured}
            onEdit={onEdit}
            onShare={onShare}
            onHistory={onHistory}
            isPremium={isPremium}
          />
        ))}
      </div>

      {/* Video cards - mobile */}
      <div className="md:hidden space-y-3">
        {videos.map((video) => (
          <VideoCardMobile
            key={video.id}
            video={video}
            onDelete={onDelete}
            onToggleFeatured={onToggleFeatured}
            onEdit={onEdit}
            onShare={onShare}
            onHistory={onHistory}
            isPremium={isPremium}
          />
        ))}
      </div>
    </div>
  );
}

/* ─── Desktop Card ─── */
function VideoCardDesktop({
  video,
  onDelete,
  onToggleFeatured,
  onEdit,
  onShare,
  onHistory,
  isPremium,
}: {
  video: AdminVideo;
  onDelete: (v: AdminVideo) => void;
  onToggleFeatured: (v: AdminVideo) => void;
  onEdit: (v: AdminVideo) => void;
  onShare: (v: AdminVideo) => void;
  onHistory: (v: AdminVideo) => void;
  isPremium: boolean;
}) {
  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden group hover:border-background-300 transition-all">
      {/* Thumbnail */}
      <div className="relative aspect-video w-full overflow-hidden bg-background-200">
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          className="w-full h-full object-cover object-top"
        />
        {/* Play overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-12 h-12 rounded-full bg-background-50/90 flex items-center justify-center">
            <i className="ri-play-fill text-foreground-900 text-xl ml-0.5"></i>
          </div>
        </div>

        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/70 text-background-50 text-[10px] font-semibold">
          {video.duration}
        </div>

        {/* From Live badge */}
        {video.derivedFromLiveId && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-red-600/90 text-background-50 text-[9px] font-semibold flex items-center gap-1">
            <i className="ri-live-line text-[10px]"></i>
            Live
          </div>
        )}

        {/* Featured star */}
        {video.isFeatured && (
          <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
            <i className="ri-star-fill text-background-50 text-[10px]"></i>
          </div>
        )}

        {/* Source badge */}
        <div className="absolute top-2 right-2 flex items-center gap-1.5">
          <span className="px-2 py-0.5 rounded-md bg-background-50/90 text-foreground-700 text-[9px] font-semibold flex items-center gap-1">
            <i className={`${sourceTypeIcons[video.sourceType]} text-[10px]`}></i>
            {video.sourceType === 'upload' ? 'Subido' : video.sourceType === 'youtube' ? 'YT' : 'FB'}
          </span>
        </div>
      </div>

      {/* Card body */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="text-sm font-bold text-foreground-950 leading-tight line-clamp-2 flex-1">
            {video.title}
          </h4>
          <span
            className={`shrink-0 px-2 py-0.5 rounded-lg text-[9px] font-semibold whitespace-nowrap ${
              video.status === 'publicado'
                ? 'bg-emerald-100 text-emerald-700'
                : video.status === 'programado'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-foreground-200 text-foreground-600'
            }`}
          >
            <i
              className={`${
                video.status === 'publicado'
                  ? 'ri-check-line'
                  : video.status === 'programado'
                  ? 'ri-time-line'
                  : 'ri-draft-line'
              } text-[9px] mr-0.5`}
            ></i>
            {statusLabels[video.status]}
          </span>
        </div>

        <p className="text-xs text-foreground-500 leading-relaxed line-clamp-2 mb-3">
          {video.description}
        </p>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[10px] text-foreground-400 mb-3">
          <span className="flex items-center gap-1">
            <i className={`${categoryIcons[video.category]} text-[10px]`}></i>
            {categoryLabels[video.category]}
          </span>
          <span>{video.fileSize}</span>
          {video.scheduledAt && (
            <span className="flex items-center gap-1 text-amber-600">
              <i className="ri-calendar-line text-[10px]"></i>
              Prog: {new Date(video.scheduledAt).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 pt-3 border-t border-background-100">
          <button
            onClick={() => onToggleFeatured(video)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
              video.isFeatured
                ? 'bg-amber-100 text-amber-500 hover:bg-amber-200'
                : 'bg-background-100 text-foreground-400 hover:bg-background-200'
            }`}
            title={video.isFeatured ? 'Quitar destacado' : 'Marcar destacado'}
          >
            <i className={`${video.isFeatured ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
          </button>
          <button
            onClick={() => onShare(video)}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-primary-100 hover:text-primary-600 transition-colors"
            title="Compartir"
          >
            <i className="ri-share-forward-line text-sm"></i>
          </button>
          {video.sourceType !== 'upload' && video.sourceUrl && (
            <a
              href={video.sourceUrl}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-primary-100 hover:text-primary-600 transition-colors"
              title="Ver fuente"
            >
              <i className="ri-external-link-line text-sm"></i>
            </a>
          )}
          <button
            onClick={() => onHistory(video)}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-secondary-100 hover:text-secondary-600 transition-colors"
            title="Ver historial"
          >
            <i className="ri-history-line text-sm"></i>
          </button>
          <div className="flex-1"></div>
          <button
            onClick={() => onEdit(video)}
            className={`w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer transition-colors ${
              isPremium
                ? 'bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-foreground-600'
                : 'bg-background-100 text-foreground-300 opacity-60'
            }`}
            title={isPremium ? 'Editar' : 'Requiere Premium'}
          >
            <i className="ri-pencil-line text-sm"></i>
          </button>
          <button
            onClick={() => onDelete(video)}
            className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer text-foreground-400 hover:bg-red-100 hover:text-red-600 transition-colors"
            title="Eliminar"
          >
            <i className="ri-delete-bin-line text-sm"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Mobile Card ─── */
function VideoCardMobile({
  video,
  onDelete,
  onToggleFeatured,
  onEdit,
  onShare,
  onHistory,
  isPremium,
}: {
  video: AdminVideo;
  onDelete: (v: AdminVideo) => void;
  onToggleFeatured: (v: AdminVideo) => void;
  onEdit: (v: AdminVideo) => void;
  onShare: (v: AdminVideo) => void;
  onHistory: (v: AdminVideo) => void;
  isPremium: boolean;
}) {
  return (
    <div className="bg-background-50 rounded-2xl border border-background-200 overflow-hidden">
      {/* Thumbnail row */}
      <div className="relative aspect-video w-full overflow-hidden bg-background-200">
        <img
          src={video.thumbnailUrl}
          alt={video.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
          <span className="px-2 py-0.5 rounded-md bg-black/70 text-background-50 text-[10px] font-semibold">
            {video.duration}
          </span>
          {video.derivedFromLiveId && (
            <span className="px-2 py-0.5 rounded-md bg-red-600/90 text-background-50 text-[9px] font-semibold flex items-center gap-1">
              <i className="ri-live-line text-[10px]"></i>
              Live
            </span>
          )}
          <span className="px-2 py-0.5 rounded-md bg-background-50/90 text-foreground-700 text-[9px] font-semibold flex items-center gap-1">
            <i className={`${sourceTypeIcons[video.sourceType]} text-[10px]`}></i>
            {video.sourceType === 'upload' ? 'Subido' : video.sourceType === 'youtube' ? 'YT' : 'FB'}
          </span>
        </div>
        {video.isFeatured && (
          <div className="absolute top-2 left-2 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center">
            <i className="ri-star-fill text-background-50 text-[10px]"></i>
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="text-sm font-bold text-foreground-950 leading-tight line-clamp-2 flex-1">
            {video.title}
          </h4>
          <span
            className={`shrink-0 px-2 py-0.5 rounded-lg text-[9px] font-semibold whitespace-nowrap ${
              video.status === 'publicado'
                ? 'bg-emerald-100 text-emerald-700'
                : video.status === 'programado'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-foreground-200 text-foreground-600'
            }`}
          >
            {statusLabels[video.status]}
          </span>
        </div>

        <p className="text-[11px] text-foreground-500 leading-relaxed line-clamp-2 mb-2">
          {video.description}
        </p>

        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[9px] text-foreground-400 mb-3">
          <span className="flex items-center gap-1">
            <i className={`${categoryIcons[video.category]} text-[10px]`}></i>
            {categoryLabels[video.category]}
          </span>
          <span>{video.fileSize}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2 border-t border-background-100">
          <button
            onClick={() => onToggleFeatured(video)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-colors ${
              video.isFeatured
                ? 'bg-amber-100 text-amber-600'
                : 'bg-background-100 text-foreground-500'
            }`}
          >
            <i className={`${video.isFeatured ? 'ri-star-fill' : 'ri-star-line'} text-xs`}></i>
            {video.isFeatured ? 'Destacado' : 'Destacar'}
          </button>
          <button
            onClick={() => onShare(video)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold cursor-pointer hover:bg-primary-100 hover:text-primary-600 transition-colors"
          >
            <i className="ri-share-forward-line text-xs"></i>
            Compartir
          </button>
          <button
            onClick={() => onHistory(video)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-[10px] font-semibold cursor-pointer hover:bg-secondary-100 hover:text-secondary-600 transition-colors"
          >
            <i className="ri-history-line text-xs"></i>
            Historial
          </button>
          <div className="flex-1"></div>
          <button
            onClick={() => onEdit(video)}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-semibold cursor-pointer transition-colors ${
              isPremium
                ? 'bg-background-100 text-foreground-500 hover:bg-background-200'
                : 'bg-background-100 text-foreground-300 opacity-60'
            }`}
          >
            <i className="ri-pencil-line text-xs"></i>
            Editar
          </button>
          <button
            onClick={() => onDelete(video)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-background-100 text-red-500 text-[10px] font-semibold cursor-pointer hover:bg-red-50 transition-colors"
          >
            <i className="ri-delete-bin-line text-xs"></i>
            Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}